import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Formik, Form, FieldArray, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ProportionType } from '../types';
import { ReactComponent as DeleteIcon } from 'assets/icons/trash.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import Modal from 'components/Modal/Modal';
import {
  generateCondition,
  reverseGenerateCondition,
} from '../LogicCreator/utils';
import Conditions from '../LogicCreator/ConditionCreator/Conditions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e3e5e8;
  background-color: white;
  border-radius: 4px;
  margin-top: 1rem;
  padding: 1rem;
`;

const Content = styled.div`
  display: flex;
  margin-top: 2rem;
  flex-direction: column;
`;

const Button = styled.button`
  height: 1.7rem;
  width: 8rem;
  outline: none;
  border: none;
  background-color: transparent !important;
  max-width: 10rem;
  margin-right: 0.5rem;
  color: rgb(31, 93, 234) !important;
  border: 1px solid rgb(31, 93, 234);
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
`;

const Proportion = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0.2rem 0.5rem;
  border: 1px solid #e3e5e8;
  margin: 0.2rem 0;
  border-radius: 4px;
`;

const ProportionContainer = styled.div`
  width: 50rem;
  height: 40rem;
  background-color: white;

  & > div {
    height: calc(100% - 8rem);
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  height: 4rem;
  border-bottom: 1px solid #e3e5e8;
  flex-direction: column;
`;

const RemoveIcon = styled(DeleteIcon)`
  cursor: pointer;
`;

const TitleInput = styled.input`
  font-size: 1.125rem;
  border: none;
  border-bottom: 1px solid black;
  outline: none;
  width: 80%;
`;

const Footer = styled.footer`
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e3e5e8;
  height: 4rem;
  padding: 0 1rem;
`;

const Error = styled.span`
  color: red;
  font-size: 0.875rem;
`;

const Icons = styled.div`
  display: flex;
  align-items: center;
  svg {
    width: 1rem;
    margin-left: 0.5rem;
    cursor: pointer;
  }
`;

const LogicText = styled.span`
  max-width: 40rem;
`;

interface Props {
  push: (proportion: any) => void;
  remove: (index: number) => void;
  questionsList: string[];
  proportions: ProportionType[];
  name: string;
  setFieldValue: (name: string, value: any) => void;
}

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Required')
    .matches(/^\S*$/g, '* Please remove all whitespaces'),
  proportions: Yup.array().of(
    Yup.object().shape({
      question: Yup.string().required('Required'),
      logicOperator: Yup.string().required('Required'),
      equals: Yup.string().when('logicOperator', (val: string) => {
        const isRequired = val !== '!empty' && val !== 'empty';
        if (isRequired) {
          return Yup.string().required('Required');
        }
        return Yup.string();
      }),
    })
  ),
});

type EditedProportion = {
  proportions: {
    question: string;
    logic: null | string;
  }[];
  title: string;
  id: null | number;
};

const ProportionCreator = ({
  push,
  remove,
  questionsList,
  proportions,
  name,
  setFieldValue,
}: Props) => {
  const [isCreatorOpen, setIsOpen] = useState(false);
  const [editedProportion, setEditedProportion] = useState<EditedProportion>({
    proportions: [],
    title: '',
    id: null,
  });

  const areProportions = proportions.length > 0;

  const addNewProportion = (values: any) => {
    const { title, proportions } = values;

    if (editedProportion.id !== null) {
      setFieldValue(`${name}.${editedProportion.id}`, {
        name: title,
        logic: generateCondition(proportions),
      });
    } else {
      push({
        name: title,
        logic: generateCondition(proportions),
      });
    }

    setEditedProportion({
      proportions: [],
      title: '',
      id: null,
    });
    setIsOpen(false);
  };

  return (
    <Container>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Add proportion
      </Button>
      <Modal open={isCreatorOpen} disableBackdropClick>
        <Formik
          initialValues={{
            proportions: editedProportion.proportions,
            title: editedProportion.title,
          }}
          onSubmit={addNewProportion}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, values, setFieldValue }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <ProportionContainer>
                  <Header>
                    <Field
                      name="title"
                      as={TitleInput}
                      placeholder="Proportion title"
                    />
                    <ErrorMessage name="title" component={Error} />
                  </Header>

                  <FieldArray name="proportions">
                    {({ remove, push }) => {
                      return (
                        <Conditions
                          values={values.proportions}
                          push={push}
                          remove={remove}
                          questionsList={questionsList}
                          fieldName="proportions"
                          setFieldValue={setFieldValue}
                        />
                      );
                    }}
                  </FieldArray>
                  <Footer>
                    <Button type="submit">Save</Button>
                    <Button onClick={() => setIsOpen(false)} type="button">
                      Cancel
                    </Button>
                  </Footer>
                </ProportionContainer>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      {areProportions && (
        <Content>
          {proportions.map(({ name, logic }, index: number) => {
            return (
              <Proportion key={name}>
                <span>{name}</span>
                <LogicText>{logic}</LogicText>
                <Icons>
                  <span
                    onClick={() => {
                      setEditedProportion({
                        proportions: reverseGenerateCondition(logic),
                        title: name,
                        id: index,
                      });

                      setIsOpen(true);
                    }}
                  >
                    <EditIcon />
                  </span>
                  <span onClick={() => remove(index)}>
                    <RemoveIcon />
                  </span>
                </Icons>
              </Proportion>
            );
          })}
        </Content>
      )}
    </Container>
  );
};

export default ProportionCreator;
